import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { ChevronDown, Menu, Phone, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Logo } from "./Logo";
import { BUSINESS, NAV } from "../data/site";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const DesktopItem = ({ item }) => {
  const [open, setOpen] = useState(false);
  if (!item.children) {
    return (
      <NavLink to={item.to} data-testid={`nav-${slug(item.label)}-link`}
        className={({ isActive }) => `relative py-2 text-sm font-semibold transition-colors hover:text-white ${isActive ? "text-white" : "text-slate-300"}`}>
        {item.label}
      </NavLink>
    );
  }
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <Link to={item.to} data-testid={`nav-${slug(item.label)}-link`} onFocus={() => setOpen(true)}
        className="flex items-center gap-1 py-2 text-sm font-semibold text-slate-300 transition-colors hover:text-white">
        {item.label} <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </Link>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }} transition={{ duration: 0.2 }}
            className="absolute left-1/2 top-full w-64 -translate-x-1/2 pt-3">
            <div className="rounded-lg border border-white/10 bg-navy-900 p-2 shadow-2xl">
              {item.children.map((c) => (
                <Link key={c.to} to={c.to} onClick={() => setOpen(false)} data-testid={`nav-sub-${slug(c.label)}-link`}
                  className="block rounded-md px-3 py-2.5 text-sm text-slate-200 transition-colors hover:bg-white/5 hover:text-flame">
                  {c.label}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const MobileMenu = ({ onClose }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    className="fixed inset-0 z-[60] overflow-y-auto bg-navy px-6 pb-32 pt-24 lg:hidden" data-testid="mobile-menu" data-lenis-prevent>
    <nav className="space-y-6">
      {NAV.map((item) => (
        <div key={item.label}>
          <Link to={item.to} onClick={onClose} className="font-display text-3xl font-bold text-white" data-testid={`mobile-nav-${slug(item.label)}-link`}>{item.label}</Link>
          {item.children && (
            <div className="mt-3 grid gap-2 border-l border-white/15 pl-4">
              {item.children.slice(1).map((c) => (
                <Link key={c.to} to={c.to} onClick={onClose} className="text-base text-slate-300 hover:text-flame">{c.label}</Link>
              ))}
            </div>
          )}
        </div>
      ))}
      <Link to="/reviews" onClick={onClose} className="block font-display text-3xl font-bold text-white">Reviews</Link>
      <Link to="/service-areas" onClick={onClose} className="block font-display text-3xl font-bold text-white" data-testid="mobile-nav-service-areas-link">Service areas</Link>
      <Link to="/contact" onClick={onClose} className="block font-display text-3xl font-bold text-white">Contact</Link>
    </nav>
    <div className="mt-10 grid gap-3">
      <Link to="/book" onClick={onClose} className="btn-primary" data-testid="mobile-menu-book-btn">Book Free Assessment</Link>
      <a href={BUSINESS.phoneHref} className="btn-ghost-dark" data-testid="mobile-menu-call-btn"><Phone className="h-4 w-4" /> Call {BUSINESS.phone}</a>
    </div>
  </motion.div>
);

export const Header = () => {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-50 border-b border-white/10 bg-navy/90 backdrop-blur-xl" data-testid="site-header">
      <div className="container-x flex h-[72px] items-center justify-between gap-6">
        <Link to="/" aria-label="Amora Painting home" data-testid="header-logo-link" className="relative z-[70]"><Logo /></Link>
        <nav className="hidden items-center gap-7 lg:flex" aria-label="Main">
          {NAV.map((i) => <DesktopItem key={i.label} item={i} />)}
        </nav>
        <div className="flex items-center gap-2">
          <a href={BUSINESS.phoneHref} data-testid="header-call-btn"
            className="hidden items-center gap-2 rounded-full border border-white/20 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:border-flame hover:text-flame md:inline-flex">
            <Phone className="h-4 w-4" /> Call {BUSINESS.phone}
          </a>
          <Link to="/book" data-testid="nav-book-assessment-btn" className="btn-primary hidden !px-5 !py-2.5 sm:inline-flex">Book Free Assessment</Link>
          <button type="button" onClick={() => setOpen((o) => !o)} aria-label={open ? "Close menu" : "Open menu"} data-testid="mobile-menu-toggle"
            className="relative z-[70] grid h-11 w-11 place-items-center rounded-full border border-white/20 text-white lg:hidden">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      <AnimatePresence>{open && <MobileMenu onClose={() => setOpen(false)} />}</AnimatePresence>
    </header>
  );
};
